import React, { useState } from "react";
import { useI18n } from "../../i18n";
import CashKeypad from "./CashKeypad";
import CompleteSaleButton from "./CompleteSaleButton";
import { formatAmount, quickCashOptions } from "../utils/money";
import { hasTab, paymentState } from "../utils/payment";
import CreditTabPicker from "./CreditTabPicker";
import { pressAmountKey, sanitizeAmount } from "../utils/amountEntry";

const METHODS = [
  { id: "cash", icon: "💵" },
  { id: "card", icon: "💳" },
  { id: "mobile_payment", icon: "📱" },
];

const CREDIT_METHOD = { id: "credit", icon: "🤝" };

/**
 * Payment step: method, cash tendered and the change to hand back.
 *
 * Cash is preselected and an empty tendered field means "exact", so the
 * fastest sale is a single tap on Complete.
 *
 * `compact` is used by the side panel on large screens, where the ticket lines
 * share the column: the amount becomes typeable, the keypad is summoned only
 * when a touch device needs it, and the controls are tightened so the whole
 * ticket normally fits without scrolling. That panel also pins the complete
 * button itself, hence `showCompleteButton`.
 *
 * `collapsibleKeypad` keeps the phone-sized controls but folds the keypad away
 * until ⌨ is tapped. The phone sheet scrolls the basket and the payment
 * together, and Exact or a note button covers most cash sales without it.
 */
const PaymentSection = ({
  totals,
  currency,
  method,
  onMethodChange,
  tendered,
  onTenderedChange,
  submitting,
  disabled,
  compact = false,
  showCompleteButton = true,
  collapsibleKeypad = false,
  onComplete,
  customer,
  canExtendCredit = false,
  creditTab = null,
  onCreditTabChange,
}) => {
  const { t } = useI18n();
  const [keypadOpen, setKeypadOpen] = useState(false);
  const {
    isCash,
    isCredit,
    changeDue,
    creditRemaining,
    amountPaid,
    isShort,
    isCreditBlocked,
  } = paymentState(method, tendered, totals.total, hasTab(customer, creditTab));
  const methods = canExtendCredit ? [...METHODS, CREDIT_METHOD] : METHODS;

  const handleKey = (key) => onTenderedChange(pressAmountKey(tendered, key));

  const keypadToggle = (size) => (
    <button
      type="button"
      onClick={() => setKeypadOpen((open) => !open)}
      aria-label={
        keypadOpen ? t("pos.payment.hideKeypad") : t("pos.payment.typeCash")
      }
      aria-expanded={keypadOpen}
      className={`${size} rounded-lg border transition-colors ${
        keypadOpen
          ? "bg-primary-500 border-primary-400 text-white"
          : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-700"
      }`}
    >
      ⌨
    </button>
  );

  return (
    <div className={compact ? "p-3 space-y-2.5" : "p-4 space-y-3"}>
      <div
        className={`grid gap-2 ${methods.length === 4 ? "grid-cols-4" : "grid-cols-3"}`}
      >
        {methods.map((paymentMethod) => {
          const isActive = paymentMethod.id === method;

          return (
            <button
              key={paymentMethod.id}
              type="button"
              onClick={() => onMethodChange(paymentMethod.id)}
              className={`rounded-xl border font-semibold text-sm flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${
                compact ? "h-12" : "h-14"
              } ${
                isActive
                  ? "bg-primary-500 border-primary-400 text-white shadow-lg shadow-primary-500/20"
                  : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
              }`}
            >
              <span className="text-lg leading-none">{paymentMethod.icon}</span>
              {t(`common.payment.${paymentMethod.id}`)}
            </button>
          );
        })}
      </div>

      {isCash && (
        <div className={compact ? "space-y-2.5" : "space-y-3"}>
          <div className="rounded-2xl bg-slate-800/60 border border-slate-700 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t("pos.payment.cashReceived")}
              </span>
              {compact ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    dir="ltr"
                    value={tendered}
                    placeholder={formatAmount(totals.total)}
                    onChange={(event) =>
                      onTenderedChange(sanitizeAmount(event.target.value))
                    }
                    aria-label={t("pos.payment.cashReceived")}
                    className={`w-24 bg-transparent text-end text-xl font-extrabold tabular-nums focus:outline-none placeholder-slate-500 ${
                      isShort ? "text-red-400" : "text-white"
                    }`}
                  />
                  {keypadToggle("w-9 h-9 text-sm")}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span
                    className={`text-2xl font-extrabold tabular-nums ${
                      isShort ? "text-red-400" : "text-white"
                    }`}
                  >
                    {tendered || formatAmount(totals.total)}
                  </span>
                  {collapsibleKeypad && keypadToggle("w-10 h-10 text-base")}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t("pos.payment.change")}
              </span>
              <span
                className={`font-extrabold text-amber-400 tabular-nums ${
                  compact ? "text-xl" : "text-2xl"
                }`}
              >
                {formatAmount(changeDue)}
                <span className="ms-1.5 text-xs font-semibold text-slate-400">
                  {currency}
                </span>
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onTenderedChange("")}
              className={`flex-1 rounded-xl text-sm font-bold border transition-all active:scale-95 ${
                compact ? "h-10" : "h-11"
              } ${
                tendered === ""
                  ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                  : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {t("pos.payment.exact")}
            </button>
            {quickCashOptions(totals.total).map((amount) => (
              <button
                key={amount}
                type="button"
                onClick={() => onTenderedChange(String(amount))}
                className={`flex-1 rounded-xl bg-slate-800 border border-slate-700 text-sm font-bold text-slate-200 hover:bg-slate-700 active:scale-95 transition-all tabular-nums ${
                  compact ? "h-10" : "h-11"
                }`}
              >
                {amount}
              </button>
            ))}
          </div>

          {((!compact && !collapsibleKeypad) || keypadOpen) && (
            <CashKeypad onPress={handleKey} compact={compact} />
          )}
        </div>
      )}

      {isCredit && (
        <div className={compact ? "space-y-2.5" : "space-y-3"}>
          <div className="rounded-2xl bg-slate-800/60 border border-slate-700 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t("pos.payment.creditReceivedNow")}
              </span>
              <input
                type="text"
                inputMode="decimal"
                dir="ltr"
                value={tendered}
                placeholder="0"
                onChange={(event) =>
                  onTenderedChange(sanitizeAmount(event.target.value))
                }
                aria-label={t("pos.payment.creditReceivedNow")}
                className="w-24 bg-transparent text-end text-xl font-extrabold tabular-nums focus:outline-none placeholder-slate-500 text-white"
              />
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t("pos.payment.creditStays")}
              </span>
              <span
                className={`font-extrabold text-amber-400 tabular-nums ${
                  compact ? "text-xl" : "text-2xl"
                }`}
              >
                {formatAmount(creditRemaining)}
                <span className="ms-1.5 text-xs font-semibold text-slate-400">
                  {currency}
                </span>
              </span>
            </div>
          </div>

          <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3 space-y-1">
            <p className="text-sm font-semibold text-amber-300">
              {amountPaid > 0
                ? t("pos.payment.creditPartialNote")
                : t("pos.payment.creditNote")}
            </p>
            {customer && (
              <p className="text-xs text-amber-200/80">
                {t("pos.payment.creditFor", { name: customer.name })}
              </p>
            )}
          </div>

          {/* A scanned card already says whose tab; otherwise name it */}
          {!customer && (
            <div className="rounded-2xl bg-slate-800/60 border border-slate-700 p-3">
              <CreditTabPicker
                value={creditTab}
                onChange={onCreditTabChange}
                currency={currency}
              />
            </div>
          )}
        </div>
      )}

      {isShort && (
        <p className="text-sm text-red-400 font-semibold text-center">
          {t("pos.payment.short")}
        </p>
      )}

      {showCompleteButton && (
        <CompleteSaleButton
          total={totals.total}
          currency={currency}
          submitting={submitting}
          disabled={disabled || submitting || isShort || isCreditBlocked}
          onClick={onComplete}
        />
      )}
    </div>
  );
};

export default PaymentSection;
